import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { formatCurrency as formatCurrencyUtil } from '../utils/currencyFormatter';
import { ArrowLeft, TrendingUp, AlertTriangle, Gem, Flame, RefreshCw, Wallet, CheckCircle2, CircleAlert, PieChart, Calendar, Package, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import TutorialVideoButton from '../components/TutorialVideoButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

// ========================================
// COMPONENTE: CatalogHealthHeader
// Métricas de Salud del Catálogo y Eficiencia
// ========================================
const CatalogHealthHeader = ({ products, formatCurrency }) => {
  // Calcular métricas de salud del catálogo
  const healthMetrics = useMemo(() => {
    if (!products || products.length === 0) {
      return {
        totalContribution: 0,
        killers: 0,
        regulares: 0,
        criticos: 0,
        totalProducts: 0,
        productsWithCogs: 0,
        cogsProgress: 0
      };
    }

    let totalContribution = 0;
    let killers = 0;
    let regulares = 0;
    let criticos = 0;
    let productsWithCogs = 0;

    products.forEach(p => {
      // Suma del margen de contribución
      totalContribution += p.contribution_margin || 0;
      
      // Clasificación por margen %
      const marginPct = p.margin_pct || 0;
      if (marginPct >= 30) killers++;
      else if (marginPct >= 15) regulares++;
      else criticos++;
      
      // Conteo de productos con COGS
      if (p.cogs_unit && p.cogs_unit > 0) productsWithCogs++;
    });

    const totalProducts = products.length;
    const cogsProgress = totalProducts > 0 ? Math.round((productsWithCogs / totalProducts) * 100) : 0;

    return {
      totalContribution,
      killers,
      regulares,
      criticos,
      totalProducts,
      productsWithCogs,
      cogsProgress
    };
  }, [products]);

  const missingCogs = healthMetrics.totalProducts - healthMetrics.productsWithCogs;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {/* SECCIÓN 1: Margen de Contribución Total */}
      <div className="relative overflow-hidden bg-card/60 backdrop-blur-md border border-border/50 rounded-2xl p-5">
        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-emerald-500/20 rounded-xl">
              <Wallet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Margen de Contribución Total</h3>
              <p className="text-[10px] text-muted-foreground/70">Disponible para Gastos Operativos</p>
            </div>
          </div>
          <p className={`text-3xl font-bold tracking-tight ${healthMetrics.totalContribution >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatCurrency(healthMetrics.totalContribution)}
          </p>
        </div>
      </div>

      {/* SECCIÓN 2: Salud del Portafolio */}
      <div className="relative overflow-hidden bg-card/60 backdrop-blur-md border border-border/50 rounded-2xl p-5">
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-blue-500/20 rounded-xl">
              <PieChart className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Salud del Portafolio</h3>
              <p className="text-[10px] text-muted-foreground/70">Distribución por rentabilidad</p>
            </div>
          </div>
          
          {/* Pills de distribución */}
          <div className="flex gap-2">
            {/* Killers */}
            <div className="flex-1 bg-green-500/10 border border-green-500/30 rounded-xl p-3 text-center transition-all hover:bg-green-500/20 hover:scale-[1.02]">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <span className="text-lg">🟢</span>
                <span className="text-[10px] font-medium text-green-600 dark:text-green-400 uppercase tracking-wide">Killers</span>
              </div>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{healthMetrics.killers}</p>
              <p className="text-[9px] text-green-700/70 dark:text-green-500/70">≥30%</p>
            </div>
            
            {/* Regulares */}
            <div className="flex-1 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-center transition-all hover:bg-yellow-500/20 hover:scale-[1.02]">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <span className="text-lg">🟡</span>
                <span className="text-[10px] font-medium text-yellow-600 dark:text-yellow-400 uppercase tracking-wide">Regulares</span>
              </div>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{healthMetrics.regulares}</p>
              <p className="text-[9px] text-yellow-700/70 dark:text-yellow-500/70">15-29%</p>
            </div>
            
            {/* Críticos */}
            <div className="flex-1 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-center transition-all hover:bg-red-500/20 hover:scale-[1.02]">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <span className="text-lg">🔴</span>
                <span className="text-[10px] font-medium text-red-600 dark:text-red-400 uppercase tracking-wide">Críticos</span>
              </div>
              <p className="text-2xl font-extrabold text-red-700 dark:text-red-500">{healthMetrics.criticos}</p>
              <p className="text-[9px] text-red-700/70 dark:text-red-500/70">&lt;15%</p>
            </div>
          </div>
        </div>
      </div>

      {/* SECCIÓN 3: Completitud de Costos */}
      <div className="relative overflow-hidden bg-card/60 backdrop-blur-md border border-border/50 rounded-2xl p-5">
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2.5 rounded-xl ${healthMetrics.cogsProgress === 100 ? 'bg-green-500/20' : 'bg-amber-500/20'}`}>
              {healthMetrics.cogsProgress === 100 ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              ) : (
                <CircleAlert className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              )}
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Completitud de Costos</h3>
              <p className="text-[10px] text-muted-foreground/70">Integridad de datos COGS</p>
            </div>
          </div>
          
          {/* Barra de Progreso Circular + Info */}
          <div className="flex items-center gap-4">
            {/* Radial Progress */}
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 transform -rotate-90">
                {/* Background circle */}
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  className="text-border"
                />
                {/* Progress circle */}
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  className={healthMetrics.cogsProgress === 100 ? 'text-green-700 dark:text-green-500' : healthMetrics.cogsProgress >= 70 ? 'text-amber-700 dark:text-amber-500' : 'text-red-700 dark:text-red-500'}
                  strokeDasharray={`${healthMetrics.cogsProgress * 1.76} 176`}
                  style={{ transition: 'stroke-dasharray 0.5s ease-in-out' }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-sm font-bold ${healthMetrics.cogsProgress === 100 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {healthMetrics.cogsProgress}%
                </span>
              </div>
            </div>
            
            {/* Info Text */}
            <div className="flex-1">
              {healthMetrics.cogsProgress === 100 ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">Data Completa</span>
                  <span className="text-lg">✅</span>
                </div>
              ) : (
                <>
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                    Faltan <span className="text-lg font-bold">{missingCogs}</span> productos
                  </p>
                  <p className="text-[10px] text-muted-foreground/70">sin costo asignado</p>
                </>
              )}
              <p className="text-[10px] text-muted-foreground/70 mt-1">
                {healthMetrics.productsWithCogs} de {healthMetrics.totalProducts} con COGS
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente Badge de Margen
const MarginBadge = ({ value, status }) => {
  const colors = {
    green: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30',
    red: 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-bold border ${colors[status]}`}>
      {value.toFixed(1)}%
    </span>
  );
};

// Componente Input COGS
const CogsInput = ({ itemId, sku, value, onUpdate }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);
  
  const handleSave = async () => {
    const newValue = parseFloat(localValue) || 0;
    if (newValue === value) return;
    
    setIsSaving(true);
    try {
      await apiClient.put(`/api/profitability/cogs/${encodeURIComponent(sku)}`, { cogs: newValue });
      onUpdate(itemId, newValue);
      toast.success('COGS actualizado');
    } catch (e) {
      toast.error('Error al guardar COGS');
      setLocalValue(value || '');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    else if (e.key === 'Escape') setLocalValue(value || '');
  };
  
  const isEmpty = !value || value === 0;
  
  return (
    <div className="relative">
      <Input
        type="number"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        placeholder="Ingresar"
        className={`w-20 h-7 text-center text-xs ${
          isEmpty ? 'border-red-500/50 bg-red-500/10 placeholder-red-400' : 'border-border bg-muted'
        } ${isSaving ? 'opacity-50' : ''}`}
        data-testid={`cogs-input-${itemId}`}
      />
      {isSaving && <RefreshCw className="absolute right-1 top-1.5 w-3 h-3 text-blue-600 dark:text-blue-400 animate-spin" />}
    </div>
  );
};

// Componente Diagnóstico
const DiagnosisBadge = ({ diagnosis }) => {
  const typeColors = {
    structural: 'bg-red-900/30 text-red-700 dark:text-red-300 border-red-500/30',
    ads_problem: 'bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-500/30',
    shipping_problem: 'bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-500/30',
    opportunity: 'bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    missing_data: 'bg-muted/50 text-muted-foreground border-border/30',
    healthy: 'bg-green-900/30 text-green-700 dark:text-green-300 border-green-500/30'
  };
  
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger>
        <span className={`px-2 py-1 rounded text-[10px] font-medium border whitespace-nowrap ${typeColors[diagnosis.type]}`}>
          {diagnosis.icon} {diagnosis.message}
        </span>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs">
        <p className="text-xs">
          {diagnosis.type === 'structural' && 'El problema es estructural. Revisa precio de venta o costo del producto.'}
          {diagnosis.type === 'ads_problem' && 'La publicidad destruye la utilidad. Considera pausar o optimizar.'}
          {diagnosis.type === 'shipping_problem' && 'El costo de envío es muy alto. Considera cobrar envío.'}
          {diagnosis.type === 'opportunity' && 'Tienes margen de sobra para invertir en publicidad.'}
          {diagnosis.type === 'missing_data' && 'Ingresa el costo del producto para calcular rentabilidad.'}
          {diagnosis.type === 'healthy' && 'Este producto tiene rentabilidad saludable.'}
        </p>
      </TooltipContent>
    </Tooltip>
  );
};

// Componente Principal
const Profitability = () => {
  const { user, authToken } = useAuth();
  const { fetchWithCache, getStaleCache, invalidateCache } = useDataCache();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const today = new Date().toISOString().split('T')[0];

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  // period: '7' | '15' | '30' | 'custom_range'
  const [period, setPeriod] = useState('30');
  const [sortBy, setSortBy] = useState('top_killers');
  const [cacheInfo, setCacheInfo] = useState(null);
  const [productSearch, setProductSearch] = useState('');

  // Custom date range (only active when period === 'custom_range')
  const [customDateFrom, setCustomDateFrom] = useState(
    () => new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]
  );
  const [customDateTo, setCustomDateTo] = useState(today);
  // Applied dates (set only when the user clicks Aplicar)
  const [appliedDateFrom, setAppliedDateFrom] = useState(null);
  const [appliedDateTo, setAppliedDateTo] = useState(null);

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    const months = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
    return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
  };

  const displayDateRange = useMemo(() => {
    if (period === 'custom_range') {
      if (appliedDateFrom && appliedDateTo) {
        return `${formatDisplayDate(appliedDateFrom)} — ${formatDisplayDate(appliedDateTo)}`;
      }
      return null;
    }
    const days = parseInt(period);
    const fromDate = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
    return `${formatDisplayDate(fromDate)} — ${formatDisplayDate(today)}`;
  }, [period, appliedDateFrom, appliedDateTo, today]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildCacheKey = (p, sb, dfrom, dto) =>
    p === 'custom_range' && dfrom && dto
      ? `profitability_custom_${dfrom}_${dto}_${sb}`
      : `profitability_${p}_${sb}`;

  const buildApiUrl = (p, sb, dfrom, dto) => {
    if (p === 'custom_range' && dfrom && dto) {
      return `/api/profitability/products?period=30&sort_by=${sb}&date_from=${dfrom}&date_to=${dto}`;
    }
    return `/api/profitability/products?period=${p}&sort_by=${sb}`;
  };

  const fetchData = useCallback(async (dfrom = appliedDateFrom, dto = appliedDateTo) => {
    const cacheKey = buildCacheKey(period, sortBy, dfrom, dto);

    if (!data) {
      setLoading(true);
    }

    try {
      const stale = getStaleCache(cacheKey);
      if (stale && !stale.isStale) {
        setData(stale.data);
        setCacheInfo(stale.data?.cache_info || null);
        setLoading(false);
        return;
      }
      if (stale && !data) {
        setData(stale.data);
        setCacheInfo(stale.data?.cache_info || null);
        setLoading(false);
      }

      const freshData = await fetchWithCache(cacheKey, async () => {
        const url = buildApiUrl(period, sortBy, dfrom, dto);
        const response = await apiClient.get(url);
        return response.data;
      });

      setData(freshData);
      setCacheInfo(freshData?.cache_info || null);
    } catch (error) {
      console.error('Error:', error);
      if (!data) toast.error('Error al cargar datos de rentabilidad');
    } finally {
      setLoading(false);
    }
  }, [period, sortBy, data, appliedDateFrom, appliedDateTo, fetchWithCache, getStaleCache]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyCustomRange = async () => {
    if (!customDateFrom || !customDateTo) {
      toast.error('Selecciona las fechas de inicio y fin');
      return;
    }
    if (customDateFrom > customDateTo) {
      toast.error('La fecha de inicio debe ser anterior a la fecha fin');
      return;
    }
    if (customDateTo > today) {
      toast.error('La fecha fin no puede ser posterior a hoy');
      return;
    }
    setAppliedDateFrom(customDateFrom);
    setAppliedDateTo(customDateTo);
    await fetchData(customDateFrom, customDateTo);
  };

  // Cargar datos cuando cambia periodo o sortBy (no para custom_range — requiere Aplicar)
  const loadedPeriodRef = useRef(null);
  useEffect(() => {
    if (period === 'custom_range') return;
    fetchData();
    loadedPeriodRef.current = period + sortBy;
  }, [period, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleCogsUpdate = (itemId, newCogs) => {
    // Invalidar caché cuando se actualiza COGS
    invalidateCache('profitability');
    
    setData(prev => {
      if (!prev) return prev;
      
      const updatedProducts = prev.products.map(p => {
        if (p.item_id !== itemId) return p;
        
        const cogs_total = newCogs * p.units_sold;
        const contribution_margin = p.ingresos_netos - cogs_total - p.platform_costs - p.ads_spend;
        const margin_pct = p.ingresos_netos > 0 ? (contribution_margin / p.ingresos_netos * 100) : 0;
        
        let margin_status = 'red';
        if (margin_pct >= 30) margin_status = 'green';
        else if (margin_pct >= 15) margin_status = 'yellow';
        
        const diagnosis = getDiagnosis(margin_pct, p.acos, p.shipping_pct, newCogs);
        
        return { ...p, cogs_unit: newCogs, cogs_total, contribution_margin: Math.round(contribution_margin), margin_pct: Math.round(margin_pct * 10) / 10, margin_status, diagnosis };
      });
      
      return { ...prev, products: updatedProducts };
    });
  };
  
  const getDiagnosis = (margin_pct, acos, shipping_pct, cogs) => {
    if (margin_pct < 15 && acos < 10) return { icon: '🚨', message: 'Sube Precio', type: 'structural' };
    if (margin_pct < 15 && acos > 20) return { icon: '📉', message: 'Optimizar Ads', type: 'ads_problem' };
    if (shipping_pct > 30) return { icon: '📦', message: 'Revisar Envío', type: 'shipping_problem' };
    if (margin_pct >= 30 && acos < 10) return { icon: '🚀', message: 'Escalar Ads', type: 'opportunity' };
    if (cogs === 0) return { icon: '⚠️', message: 'Ingresa COGS', type: 'missing_data' };
    return { icon: '✅', message: 'Saludable', type: 'healthy' };
  };

  const columnTotals = useMemo(() => {
    const products = data?.products || [];
    if (products.length === 0) return null;
    const totalIngresos = products.reduce((s, p) => s + (p.ingresos_netos || 0), 0);
    const totalMargin = products.reduce((s, p) => s + (p.contribution_margin || 0), 0);
    return {
      units_sold:          products.reduce((s, p) => s + (p.units_sold || 0), 0),
      ingresos_netos:      totalIngresos,
      cogs_total:          products.reduce((s, p) => s + (p.cogs_total || 0), 0),
      comision_meli:       products.reduce((s, p) => s + (p.comision_meli || 0), 0),
      envio_gratis:        products.reduce((s, p) => s + (p.envio_gratis || 0), 0),
      retenciones:         products.reduce((s, p) => s + (p.retenciones || 0), 0),
      ads_spend:           products.reduce((s, p) => s + (p.ads_spend || 0), 0),
      contribution_margin: totalMargin,
      margin_pct:          totalIngresos > 0 ? (totalMargin / totalIngresos * 100) : 0,
    };
  }, [data?.products]);
  
  // Formato de moneda dinámico según el site del usuario
  const userSite = user?.mercado_libre_site || 'MCO';
  const formatCurrency = (value) => formatCurrencyUtil(value, userSite);
  
  const tabs = [
    { id: 'top_killers', label: 'Top Killers', icon: Flame, color: 'text-orange-600 dark:text-orange-400' },
    { id: 'criticos', label: 'Críticos', icon: AlertTriangle, color: 'text-red-600 dark:text-red-400' },
    { id: 'oportunidades', label: 'Oportunidades', icon: Gem, color: 'text-emerald-600 dark:text-emerald-400' }
  ];

  const filteredProducts = productSearch.trim()
    ? (data?.products || []).filter(p => {
        const q = productSearch.trim().toLowerCase();
        return (p.title || '').toLowerCase().includes(q) || (p.item_id || '').toLowerCase().includes(q);
      })
    : (data?.products || []);

  if (!user) { navigate('/'); return null; }
  
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background text-foreground p-4 md:p-6">
        <div className="max-w-[1800px] mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-muted rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2"><TrendingUp className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />Gestión de Rentabilidad</h1>
                <p className="text-sm text-muted-foreground">Margen de Contribución por Publicación • Meta: &gt;30%</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <TutorialVideoButton video="Rentabilidad.mp4" caption="Cómo interpretar el módulo de Rentabilidad." />
              {/* Cache status indicator */}
              {cacheInfo?.from_cache && (
                <div className="text-xs text-muted-foreground/70 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  Actualizado hace {cacheInfo.age_minutes || 0} min
                </div>
              )}

              {/* Selector de periodo */}
              <div className="relative">
                <div className="flex items-center gap-2">
                  {loading && (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-blue-400"></div>
                      <span className="text-xs">Actualizando...</span>
                    </div>
                  )}
                  <div className="bg-muted border border-border rounded-lg px-3 py-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <select
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      disabled={loading}
                      className="bg-transparent text-foreground text-sm outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="7" className="bg-popover">Últimos 7 días</option>
                      <option value="15" className="bg-popover">Últimos 15 días</option>
                      <option value="30" className="bg-popover">Últimos 30 días</option>
                      <option value="custom_range" className="bg-popover">Rango personalizado</option>
                    </select>
                  </div>
                </div>

                {displayDateRange && (
                  <p className="absolute top-full right-0 mt-1 text-xs text-muted-foreground text-right whitespace-nowrap">{displayDateRange}</p>
                )}

                {period === 'custom_range' && (
                  <div className="absolute top-full right-0 mt-2 z-50 flex items-center gap-2 bg-popover border border-border rounded-lg px-3 py-2 shadow-2xl whitespace-nowrap">
                    <input
                      type="date"
                      value={customDateFrom}
                      max={customDateTo}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      disabled={loading}
                      className="bg-transparent text-foreground text-sm outline-none cursor-pointer disabled:opacity-60"
                    />
                    <span className="text-muted-foreground/70 text-xs">—</span>
                    <input
                      type="date"
                      value={customDateTo}
                      min={customDateFrom}
                      max={today}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      disabled={loading}
                      className="bg-transparent text-foreground text-sm outline-none cursor-pointer disabled:opacity-60"
                    />
                    <button
                      onClick={handleApplyCustomRange}
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium px-3 py-1 rounded transition-colors"
                    >
                      Aplicar
                    </button>
                  </div>
                )}
              </div>

            </div>
          </div>
          
          {/* Catalog Health Header - Nuevo diseño */}
          {data?.products && (
            <CatalogHealthHeader 
              products={data.products} 
              formatCurrency={formatCurrency}
            />
          )}
          
          {/* Tabs */}
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex gap-2">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setSortBy(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${sortBy === tab.id ? 'bg-muted text-foreground border border-border' : 'bg-muted/30 text-muted-foreground hover:text-foreground'}`}>
                  <tab.icon className={`w-4 h-4 ${sortBy === tab.id ? tab.color : ''}`} />{tab.label}
                  {tab.id === 'criticos' && data?.summary?.products_critical > 0 && <span className="px-1.5 py-0.5 bg-red-500/20 text-red-600 dark:text-red-400 rounded text-[10px]">{data.summary.products_critical}</span>}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-muted-foreground/70 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Buscar por nombre o ID..."
                data-testid="profitability-search-input"
                className="h-8 w-56 pl-7 text-xs"
              />
            </div>
          </div>
          
          {/* Tabla */}
          <div className="bg-card/60 backdrop-blur-md border border-border/50 rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="profitability-table">
                  <thead className="bg-muted/70 sticky top-0 z-10">
                    <tr className="text-muted-foreground text-xs">
                      <th className="px-3 py-3 text-left">Publicación</th>
                      <th className="px-2 py-3 text-center">Uds</th>
                      <th className="px-2 py-3 text-right">Ingresos</th>
                      <th className="px-2 py-3 text-right">Precio Prom</th>
                      <th className="px-2 py-3 text-center"><Tooltip delayDuration={200}><TooltipTrigger className="cursor-help border-b border-dashed border-border">COGS</TooltipTrigger><TooltipContent>Costo del Producto</TooltipContent></Tooltip></th>
                      <th className="px-2 py-3 text-right"><Tooltip delayDuration={200}><TooltipTrigger className="cursor-help border-b border-dashed border-border">Comisión</TooltipTrigger><TooltipContent>Comisión MeLi</TooltipContent></Tooltip></th>
                      <th className="px-2 py-3 text-right"><Tooltip delayDuration={200}><TooltipTrigger className="cursor-help border-b border-dashed border-border">Envío</TooltipTrigger><TooltipContent>Costo Envío Gratis</TooltipContent></Tooltip></th>
                      <th className="px-2 py-3 text-right"><Tooltip delayDuration={200}><TooltipTrigger className="cursor-help border-b border-dashed border-border">Retenc.</TooltipTrigger><TooltipContent>Retenciones</TooltipContent></Tooltip></th>
                      <th className="px-2 py-3 text-right"><Tooltip delayDuration={200}><TooltipTrigger className="cursor-help border-b border-dashed border-border">Ads</TooltipTrigger><TooltipContent>Gasto Publicidad + ACOS</TooltipContent></Tooltip></th>
                      <th className="px-2 py-3 text-center"><Tooltip delayDuration={200}><TooltipTrigger className="cursor-help border-b border-dashed border-border">Tráfico Ads</TooltipTrigger><TooltipContent>% de visitas provenientes de Ads<br/>{'>'} 70% = Sobredosis | 40-70% = Ideal | {'<'} 40% = Motor Apagado</TooltipContent></Tooltip></th>
                      <th className="px-2 py-3 text-right">Margen $</th>
                      <th className="px-2 py-3 text-center">Margen %</th>
                      <th className="px-2 py-3 text-center">Diagnóstico</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredProducts.length === 0 && (
                      <tr><td colSpan={13} className="px-3 py-8 text-center text-muted-foreground text-xs">No se encontraron publicaciones que coincidan con "{productSearch}"</td></tr>
                    )}
                    {filteredProducts.map((p) => (
                      <tr key={p.item_id} className={`hover:bg-muted/30 transition-colors ${p.margin_status === 'red' ? 'bg-red-950/10' : ''}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {p.thumbnail && <img src={p.thumbnail} alt="" className="w-8 h-8 rounded object-cover" />}
                            <div className="min-w-0">
                              <p className="text-foreground text-xs font-medium truncate max-w-[200px]">{p.title}</p>
                              <p className="text-[10px] text-muted-foreground/70 font-mono">{p.sku} <span className="text-blue-600/50 dark:text-blue-400/50">{p.item_id}</span></p>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center text-muted-foreground text-xs">{p.units_sold}</td>
                        <td className="px-2 py-2 text-right text-foreground text-xs font-medium">{formatCurrency(p.ingresos_netos)}</td>
                        <td className="px-2 py-2 text-right text-muted-foreground text-xs">{formatCurrency(p.avg_price)}</td>
                        <td className="px-2 py-2 text-center"><CogsInput itemId={p.item_id} sku={p.sku} value={p.cogs_unit} onUpdate={handleCogsUpdate} /></td>
                        <td className="px-2 py-2 text-right text-red-600 dark:text-red-400 text-xs">-{formatCurrency(p.comision_meli)}</td>
                        <td className="px-2 py-2 text-right">
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger><span className="text-red-600 dark:text-red-400 text-xs">-{formatCurrency(p.envio_gratis)}</span></TooltipTrigger>
                            <TooltipContent><p className="text-xs">{p.shipping_pct.toFixed(1)}% del ingreso</p></TooltipContent>
                          </Tooltip>
                        </td>
                        <td className="px-2 py-2 text-right text-red-600 dark:text-red-400 text-xs">-{formatCurrency(p.retenciones)}</td>
                        <td className="px-2 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span className={`text-xs ${p.ads_spend > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-muted-foreground/70'}`}>{p.ads_spend > 0 ? `-${formatCurrency(p.ads_spend)}` : '-'}</span>
                            {p.ads_spend > 0 && <span className={`text-[9px] ${p.acos > 20 ? 'text-red-600 dark:text-red-400 font-bold' : 'text-muted-foreground/70'}`}>({p.acos}%)</span>}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          {p.ratio_ads > 0 ? (
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger>
                                <span className={`text-xs font-medium ${p.traffic_color === 'red' ? 'text-red-600 dark:text-red-400' : p.traffic_color === 'green' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                                  {p.ratio_ads}%
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs font-medium">{p.traffic_diag}</p>
                                <p className="text-[10px] text-muted-foreground">{p.ads_clicks} clicks / {p.visits} visitas</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground/70 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right"><span className={`text-xs font-bold ${p.contribution_margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{formatCurrency(p.contribution_margin)}</span></td>
                        <td className="px-2 py-2 text-center"><MarginBadge value={p.margin_pct} status={p.margin_status} /></td>
                        <td className="px-2 py-2 text-center"><DiagnosisBadge diagnosis={p.diagnosis} /></td>
                      </tr>
                    ))}
                  </tbody>
                  {columnTotals && (
                    <tfoot>
                      <tr className="bg-muted/90 border-t-2 border-border text-xs font-bold">
                        <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                          Totales <span className="text-muted-foreground/70 font-normal">({data.products.length} productos)</span>
                        </td>
                        <td className="px-2 py-3 text-center text-foreground">{columnTotals.units_sold.toLocaleString()}</td>
                        <td className="px-2 py-3 text-right text-foreground">{formatCurrency(columnTotals.ingresos_netos)}</td>
                        <td className="px-2 py-3 text-right text-muted-foreground/70">—</td>
                        <td className="px-2 py-3 text-center text-muted-foreground">{formatCurrency(columnTotals.cogs_total)}</td>
                        <td className="px-2 py-3 text-right text-red-600 dark:text-red-400">-{formatCurrency(columnTotals.comision_meli)}</td>
                        <td className="px-2 py-3 text-right text-red-600 dark:text-red-400">-{formatCurrency(columnTotals.envio_gratis)}</td>
                        <td className="px-2 py-3 text-right text-red-600 dark:text-red-400">-{formatCurrency(columnTotals.retenciones)}</td>
                        <td className="px-2 py-3 text-right text-orange-600 dark:text-orange-400">
                          {columnTotals.ads_spend > 0 ? `-${formatCurrency(columnTotals.ads_spend)}` : <span className="text-muted-foreground/70">—</span>}
                        </td>
                        <td className="px-2 py-3 text-center text-muted-foreground/70">—</td>
                        <td className="px-2 py-3 text-right">
                          <span className={columnTotals.contribution_margin >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                            {formatCurrency(columnTotals.contribution_margin)}
                          </span>
                        </td>
                        <td className="px-2 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full border ${
                            columnTotals.margin_pct >= 30 ? 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30' :
                            columnTotals.margin_pct >= 15 ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30' :
                            'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30'
                          }`}>
                            {columnTotals.margin_pct.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-2 py-3" />
                      </tr>
                    </tfoot>
                  )}
                </table>
                {data?.products?.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground/70"><Package className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No hay datos para el periodo seleccionado</p></div>
                )}
              </div>
            )}
          </div>
          
          <div className="mt-4 text-center text-xs text-muted-foreground/70">
            Ingresos Netos (igual que Dashboard) • Margen = Ingresos - COGS - Comisión - Envío - Retenciones - Ads
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Profitability;
